import { useQuery, useMutation } from '@tanstack/react-query'
import { trainerApi } from '@/api/trainer'
import { Link2, Download, ExternalLink } from 'lucide-react'

export function CourseMaterialsSection({ courseId }: { courseId: string }) {
  const { data: materials = [], isLoading } = useQuery({
    queryKey: ['course-materials', courseId],
    queryFn: () => trainerApi.getCourseMaterials(courseId).then(r => r.data),
  })

  const downloadMutation = useMutation({
    mutationFn: (materialId: string) =>
      trainerApi.getCourseMaterialDownloadUrl(courseId, materialId).then(r => r.data.url),
    onSuccess: (url) => window.open(url, '_blank'),
  })

  if (isLoading) return <p className="text-sm text-gray-400">Loading materials…</p>
  if (materials.length === 0) return <p className="text-sm text-gray-400">No materials uploaded for this course.</p>

  return (
    <div className="space-y-1.5">
      {materials.map(m => (
        <div key={m.id} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2.5">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            {m.materialType === 'Link'
              ? <Link2 size={13} className="shrink-0 text-gray-400" />
              : <Download size={13} className="shrink-0 text-gray-400" />
            }
            <p className="text-sm font-medium text-gray-800 truncate">{m.title || m.fileName}</p>
          </div>
          {m.materialType === 'Link' ? (
            <a
              href={m.url ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3 shrink-0 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <ExternalLink size={13} /> Open
            </a>
          ) : (
            <button
              onClick={() => downloadMutation.mutate(m.id)}
              disabled={downloadMutation.isPending}
              className="ml-3 shrink-0 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              <Download size={13} /> Download
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
